import { useEffect, useRef, useState } from 'react';
import {
  GOV_ANNOUNCE_MAX,
  GOV_REPORT_REASONS,
  GOV_SLOGAN_MAX,
  formatPence,
  govOf,
  type GovCandidateView,
  type GovReply,
  type GovReportReason,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { useCatalogue } from '../lib/content';
import { notify } from '../lib/alerts';
import { actionKey, londonWhen } from '../lib/format';
import { setGovBanner } from '../lib/gov';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';
import { shareLink } from '../lib/share';

// Gov, as Lagos Life's 🏛️ Gov in London reality (contracts gov.ts): the State House (this week's Mayor, the policy
// in force and their announcement), the Mayor's own office (one policy for London, an announcement on every map),
// and the week's election: the live count, voting in person at City Hall, standing, and a link to share.

const SEARCH_FROM = 6;
// "2d 4h", "3h 20m", "12m": how long until something
export function leftOf(ms: number) {
  if (ms <= 0) return 'now';
  const hours = Math.floor(ms / 3_600_000);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${days}d ${hours % 24}h`;
  if (hours >= 1) return `${hours}h ${Math.floor((ms % 3_600_000) / 60_000)}m`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m`;
}
// a candidate's ballot link: the game opens Gov on the ballot with them ready
export const voteLink = (name: string) =>
  `${window.location.origin}/?vote=${encodeURIComponent(name.replace(/^@/, ''))}`;

export function GovScreen(props: PhoneAppProps) {
  const { reply, failure, busy, load, run } = usePhoneApp(() => api.gov.state(), props);
  const [slogan, setSlogan] = useState('');
  const [words, setWords] = useState('');
  const [search, setSearch] = useState(
    props.open && props.open !== 'ballot' ? props.open.replace(/^@/, '').toLowerCase() : '',
  );
  const [now, setNow] = useState(() => Date.now());
  const ballot = useRef<HTMLElement>(null);
  // the clock for "polls close in…", and the count fresh every half a minute
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 30_000);
    const count = window.setInterval(() => void load(), 30_000);
    return () => {
      window.clearInterval(tick);
      window.clearInterval(count);
    };
  }, [load]);
  // opened from City Hall's booths or a ballot link: straight to the ballot
  const opened = Boolean(props.open);
  useEffect(() => {
    if (opened && reply) ballot.current?.scrollIntoView({ block: 'start' });
  }, [opened, reply]);
  // the banner on every map follows what Gov says now
  useEffect(() => {
    if (reply) setGovBanner({ mayor: reply.mayor, policy: policyOf(reply) });
  }, [reply]);

  return (
    <PhoneAppFrame
      id="gov"
      title="🏛️ Gov"
      className="gov-screen"
      closeLabel="Close Gov"
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={reply ? undefined : 'Opening City Hall…'}
    >
      {reply && !reply.visible && <p className="business-muted">The election is closed for now. Check back soon.</p>}
      {reply?.visible && (
        <div className="business-detail gov-detail">
          <StateHouse reply={reply} now={now} onReported={() => void load()} />
          {reply.isMayor && (
            <MayorOffice
              reply={reply}
              busy={busy}
              words={words}
              setWords={setWords}
              onPolicy={(policy) => void run(() => api.gov.policy(policy))}
              onAnnounce={() =>
                void run(() => api.gov.announce(words.trim(), actionKey('gov'))).then((ok) => ok && setWords(''))
              }
            />
          )}
          <section className="gov-race" aria-label="The election" ref={ballot}>
            <header>
              <strong>🗳️ Mayor of London · next week</strong>
              <small>
                {reply.election.paused
                  ? '⏸️ Paused for a check: voting and standing open again soon'
                  : `Polls close in ${leftOf(new Date(reply.election.closes).getTime() - now)} · Sunday 8pm`}
              </small>
            </header>
            <div className="gov-polls">
              <span aria-hidden="true">🏛️</span>
              <span>
                <b>Vote in person at {reply.election.pollingPlace.name}</b>
                <small>Royal Victoria Dock · one ballot paper, one vote</small>
              </span>
              {!reply.election.atPolls && props.onPlace && (
                <button
                  type="button"
                  className="city-card-go"
                  onClick={() => props.onPlace!(reply.election.pollingPlace.placeId)}
                >
                  Go vote
                </button>
              )}
            </div>
            {reply.election.opensForMe && (
              <p className="gov-note">🕒 New lives can vote and stand from {londonWhen(reply.election.opensForMe)}.</p>
            )}
            <Ballot
              reply={reply}
              busy={busy}
              search={search}
              setSearch={setSearch}
              onVote={(candidate) => void run(() => api.gov.vote(candidate.id))}
            />
            {reply.election.mine ? (
              <Campaign reply={reply} />
            ) : (
              <form
                className="gov-stand"
                onSubmit={(event) => {
                  event.preventDefault();
                  void run(() => api.gov.stand(slogan.trim(), actionKey('gov'))).then((ok) => ok && setSlogan(''));
                }}
              >
                <strong>Run for Mayor 🗳️</strong>
                <small>
                  {formatPence(reply.standFeePence)} to stand. The Mayor picks one policy for all of London and earns{' '}
                  {formatPence(reply.mayorPayPence)} a day for the week.
                </small>
                <input
                  aria-label="Your slogan"
                  value={slogan}
                  maxLength={GOV_SLOGAN_MAX}
                  placeholder="Your slogan, e.g. Night buses for everyone!"
                  onChange={(event) => setSlogan(event.target.value)}
                />
                <button
                  type="submit"
                  className="city-card-go"
                  disabled={
                    busy || slogan.trim().length < 3 || Boolean(reply.election.opensForMe) || reply.election.paused
                  }
                >
                  Stand · {formatPence(reply.standFeePence)}
                </button>
              </form>
            )}
          </section>
          {reply.last && (
            <p className="business-muted">
              Last week:{' '}
              {reply.last.winner
                ? `${reply.last.winner.name} won with ${reply.last.winner.votes} of ${reply.last.total} votes${
                    reply.last.tie ? ' (a tie, won by reaching the total first)' : ''
                  }.`
                : 'nobody won (no votes).'}
            </p>
          )}
        </div>
      )}
    </PhoneAppFrame>
  );
}

const policyOf = (reply: GovReply) => reply.policies.find((item) => item.id === reply.mayor?.policy) ?? null;

// this week's Mayor, the policy in force and their announcement (reportable)
function StateHouse({ reply, now, onReported }: { reply: GovReply; now: number; onReported: () => void }) {
  const [reporting, setReporting] = useState(false);
  const [sending, setSending] = useState(false);
  const mayor = reply.mayor;
  const policy = policyOf(reply);
  const report = async (reason: GovReportReason) => {
    if (!mayor?.announcement) return;
    setSending(true);
    try {
      const answer = await api.gov.report(mayor.announcement.id, reason);
      setGovBanner(answer.banner);
      notify(answer.message);
      setReporting(false);
      onReported();
    } catch (error) {
      notify(describeError(error).message);
    } finally {
      setSending(false);
    }
  };
  return (
    <section className="gov-house" aria-label="The Mayor this week">
      <span className="gov-house-icon" aria-hidden="true">
        🏛️
      </span>
      {mayor ? (
        <span className="gov-house-main">
          <small>Mayor of London · this week</small>
          <strong>{mayor.name}</strong>
          <small>Term ends in {leftOf(new Date(mayor.until).getTime() - now)}</small>
          {policy && (
            <span className="gov-in-force">
              {policy.icon} <b>{policy.label}</b> · {policy.effect}
            </span>
          )}
          {mayor.announcement && (
            <span className="gov-announcement">
              <span>📢 “{mayor.announcement.text}”</span>
              {!reply.isMayor &&
                (reporting ? (
                  <span className="gov-reasons" role="group" aria-label="What is wrong with it">
                    {(Object.keys(GOV_REPORT_REASONS) as GovReportReason[]).map((reason) => (
                      <button key={reason} type="button" disabled={sending} onClick={() => void report(reason)}>
                        {GOV_REPORT_REASONS[reason]}
                      </button>
                    ))}
                  </span>
                ) : (
                  <button type="button" className="gov-link" onClick={() => setReporting(true)}>
                    Report
                  </button>
                ))}
            </span>
          )}
        </span>
      ) : (
        <span className="gov-house-main">
          <strong>London has no Mayor this week</strong>
          <small>Stand in the election below and the city could be yours.</small>
        </span>
      )}
    </section>
  );
}

// the Mayor's own office: one policy for London (changed once a day) and an announcement on every map
function MayorOffice({
  reply,
  busy,
  words,
  setWords,
  onPolicy,
  onAnnounce,
}: {
  reply: GovReply;
  busy: boolean;
  words: string;
  setWords: (text: string) => void;
  onPolicy: (policy: GovReply['policies'][number]['id']) => void;
  onAnnounce: () => void;
}) {
  return (
    <section className="gov-office" aria-label="Your office">
      <strong>Your office, Mayor 🏛️</strong>
      <small>
        {reply.policyFrom
          ? `Pick one policy for all of London. You can change it again ${londonWhen(reply.policyFrom)}.`
          : 'Pick one policy for all of London. You can change it once a day.'}
      </small>
      <ul className="gov-policies" aria-label="Policies">
        {reply.policies.map((item) => {
          const inForce = reply.mayor?.policy === item.id;
          return (
            <li key={item.id} className={inForce ? 'in-force' : ''}>
              <span aria-hidden="true">{item.icon}</span>
              <span>
                <b>{item.label}</b>
                <small>{item.effect}</small>
              </span>
              <button
                type="button"
                className={inForce ? 'gov-chip' : 'city-card-go'}
                disabled={busy || inForce || Boolean(reply.policyFrom)}
                onClick={() => onPolicy(item.id)}
              >
                {inForce ? 'In force' : 'Make it law'}
              </button>
            </li>
          );
        })}
      </ul>
      <form
        className="gov-stand"
        onSubmit={(event) => {
          event.preventDefault();
          onAnnounce();
        }}
      >
        <strong>Announcement to every player 📢</strong>
        {reply.announceFrom && (
          <small className="gov-note">
            Your last one was taken down. You can post again {londonWhen(reply.announceFrom)}.
          </small>
        )}
        <textarea
          aria-label="Your announcement"
          rows={2}
          value={words}
          maxLength={GOV_ANNOUNCE_MAX}
          placeholder="Festival season is here! Enjoy London 🎉"
          onChange={(event) => setWords(event.target.value)}
        />
        <small>
          {words.length}/{GOV_ANNOUNCE_MAX} · shown on every map in London. Be kind: players can report it.
        </small>
        <button
          type="submit"
          className="city-card-go"
          disabled={busy || words.trim().length < 3 || Boolean(reply.announceFrom)}
        >
          Post to every map
        </button>
      </form>
    </section>
  );
}

// the ballot with the live count: search when it is long, each candidate's votes and share of them, a vote
// (at City Hall only) and their link
function Ballot({
  reply,
  busy,
  search,
  setSearch,
  onVote,
}: {
  reply: GovReply;
  busy: boolean;
  search: string;
  setSearch: (text: string) => void;
  onVote: (candidate: GovCandidateView) => void;
}) {
  const { election } = reply;
  const long = election.candidates.length > SEARCH_FROM;
  const looking = search.trim().replace(/^@/, '').toLowerCase();
  const shown =
    long && looking
      ? election.candidates.filter((item) => item.name.toLowerCase().replace(/^@/, '').includes(looking))
      : election.candidates;
  const canVote = election.atPolls && !election.opensForMe && !election.paused;
  const share = async (candidate: GovCandidateView) => {
    const done = await shareLink({
      title: `Vote ${candidate.name} for Mayor of London 🗳️`,
      url: voteLink(candidate.name),
    });
    if (done === 'copied') notify(`${candidate.name}'s ballot link copied 🔗`);
  };
  if (!election.candidates.length)
    return <p className="business-muted">No candidates yet. Be the first on the ballot.</p>;
  return (
    <>
      {long && (
        <input
          className="gov-search"
          aria-label="Search candidates"
          value={search}
          placeholder={`Search ${election.candidates.length} candidates by @username`}
          onChange={(event) => setSearch(event.target.value)}
        />
      )}
      <ul className="gov-candidates" aria-label="Candidates">
        {shown.map((candidate) => {
          const share100 = election.total ? Math.round((candidate.votes / election.total) * 100) : 0;
          const mine = election.myVote === candidate.id;
          return (
            <li key={candidate.id} className={mine ? 'my-vote' : ''}>
              <span className="gov-candidate-head">
                <b>{candidate.name}</b>
                <small>
                  {candidate.votes} · {share100}%
                </small>
              </span>
              <small>“{candidate.slogan}”</small>
              <span className="gov-bar" aria-hidden="true">
                <span style={{ width: `${share100}%` }} />
              </span>
              <span className="gov-candidate-actions">
                {mine ? (
                  <span className="gov-chip">Your vote ✓</span>
                ) : (
                  <button
                    type="button"
                    className="city-card-go"
                    disabled={busy || !canVote}
                    aria-label={`Vote for ${candidate.name}`}
                    onClick={() => onVote(candidate)}
                  >
                    Vote
                  </button>
                )}
                <button
                  type="button"
                  className="gov-link"
                  aria-label={`Share ${candidate.name}'s ballot link`}
                  onClick={() => void share(candidate)}
                >
                  🔗 Share
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      {!election.atPolls && <p className="gov-note">Votes are cast in person: go to City Hall to vote.</p>}
    </>
  );
}

// standing: how to campaign, and your own link
function Campaign({ reply }: { reply: GovReply }) {
  const ageDays = govOf(useCatalogue() ?? {}).ageDays;
  const me = reply.election.candidates.find((item) => item.id === reply.election.mine);
  return (
    <section className="gov-campaign" aria-label="Your campaign">
      <strong>You're on the ballot 📣</strong>
      {me && (
        <button
          type="button"
          className="city-card-go"
          onClick={() =>
            void shareLink({ title: 'Vote for me for Mayor of London 🗳️', url: voteLink(me.name) }).then(
              (done) => done === 'copied' && notify('Your ballot link copied 🔗'),
            )
          }
        >
          🔗 Share your ballot link
        </button>
      )}
      <ol>
        <li>Post your link on Gist and in your chats. It opens the ballot with you ready.</li>
        <li>Votes are cast in person at City Hall until Sunday 8pm. Remind your people to go.</li>
        <li>Only lives {ageDays} days old or more can vote, so get your people in early.</li>
      </ol>
    </section>
  );
}
